import type { SxProps, Theme } from '@mui/material/styles';
import { designModes } from './DesignSystem/designTokens';

// Shared member-portal styling so modals, the header and the auth pages all
// speak the same dark-teal language as the portal pages. Palette is the
// canonical dark design tokens (single source of truth in designTokens.ts).
export const PT = {
  ...designModes.dark,
  // Accents lifted from the sidebar's active row so nav + surfaces agree.
  tealSoft: 'rgba(56,232,255,0.16)',
  tealBorder: 'rgba(56,232,255,0.55)',
  tealText: '#8af5ff'
} as const;

// A dark, teal-edged dialog surface — the modal equivalent of a portal card.
export const dialogPaperSx: SxProps<Theme> = {
  borderRadius: '18px',
  border: `1px solid ${PT.borderStrong}`,
  backgroundColor: PT.surface,
  backgroundImage: `radial-gradient(120% 90% at 90% -10%, rgba(56,232,255,0.14), transparent 45%), linear-gradient(180deg, ${PT.surface} 0%, ${PT.pageSoft} 100%)`,
  boxShadow: '0 28px 80px rgba(0,0,0,0.6)',
  color: PT.text
};

// Filled teal primary action.
export const primaryBtnSx: SxProps<Theme> = {
  background: `linear-gradient(180deg, ${PT.teal}, ${PT.tealDeep})`,
  color: '#04222a',
  fontWeight: 800,
  borderRadius: '10px',
  textTransform: 'none',
  boxShadow: '0 8px 22px rgba(56,232,255,0.28)',
  '&:hover': { background: `linear-gradient(180deg, #63eeff, ${PT.teal})` },
  '&.Mui-disabled': { background: 'rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.4)' }
};

// Quiet, outlined secondary action.
export const ghostBtnSx: SxProps<Theme> = {
  color: PT.text,
  border: `1px solid ${PT.border}`,
  background: 'rgba(255,255,255,0.04)',
  borderRadius: '10px',
  textTransform: 'none',
  fontWeight: 700,
  '&:hover': { background: 'rgba(255,255,255,0.09)', borderColor: PT.tealBorder }
};

// Dark input with a teal focus ring.
export const fieldSx: SxProps<Theme> = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '10px',
    backgroundColor: 'rgba(255,255,255,0.04)',
    color: PT.text,
    '& fieldset': { borderColor: PT.border },
    '&:hover fieldset': { borderColor: 'rgba(56,232,255,0.5)' },
    '&.Mui-focused fieldset': {
      borderColor: PT.teal,
      boxShadow: '0 0 0 3px rgba(56,232,255,0.18)'
    }
  },
  '& .MuiInputLabel-root': { color: PT.textMuted },
  '& .MuiInputLabel-root.Mui-focused': { color: PT.teal },
  '& .MuiFormHelperText-root': { color: PT.textMuted }
};

// Translucent inner panel (e.g. an info block inside a modal).
export const panelSx: SxProps<Theme> = {
  borderRadius: '14px',
  border: `1px solid ${PT.border}`,
  backgroundColor: 'rgba(255,255,255,0.03)'
};

// Small uppercase eyebrow/kicker.
export const kickerSx: SxProps<Theme> = {
  font: '800 11px/1 Montserrat, sans-serif',
  letterSpacing: '0.18em',
  textTransform: 'uppercase',
  color: PT.teal
};

// The auth split's left promo pane (JoinNow) — give the page a dark ground.
export const authPageSx: SxProps<Theme> = {
  backgroundColor: PT.page,
  color: PT.text
};

// The form pane of the auth split. Applied to the <Paper> so every MUI field,
// button, link and checkbox inside picks up the portal's dark-teal treatment
// without touching each control individually.
export const authPaneSx: SxProps<Theme> = {
  backgroundColor: PT.page,
  backgroundImage: `radial-gradient(120% 60% at 100% 0%, rgba(56,232,255,0.12), transparent 45%), linear-gradient(180deg, ${PT.surface} 0%, ${PT.page} 100%)`,
  borderLeft: `1px solid ${PT.borderStrong}`,
  color: PT.text,
  '& h1, & h2, & h4, & h5, & .MuiTypography-h4, & .MuiTypography-h5, & .MuiTypography-h6': {
    color: PT.text,
    fontWeight: 800
  },
  '& .MuiOutlinedInput-root': {
    borderRadius: '10px',
    backgroundColor: 'rgba(255,255,255,0.04)',
    color: PT.text,
    '& fieldset': { borderColor: PT.border },
    '&:hover fieldset': { borderColor: 'rgba(56,232,255,0.5)' },
    '&.Mui-focused fieldset': {
      borderColor: PT.teal,
      boxShadow: '0 0 0 3px rgba(56,232,255,0.18)'
    }
  },
  '& .MuiInputLabel-root': { color: PT.textMuted },
  '& .MuiInputLabel-root.Mui-focused': { color: PT.teal },
  '& .MuiFormHelperText-root': { color: PT.textMuted },
  '& .MuiButton-contained': {
    background: `linear-gradient(180deg, ${PT.teal}, ${PT.tealDeep})`,
    color: '#04222a',
    fontWeight: 800,
    borderRadius: '10px',
    boxShadow: '0 8px 22px rgba(56,232,255,0.28)',
    '&:hover': { background: `linear-gradient(180deg, #63eeff, ${PT.teal})` },
    '&.Mui-disabled': { background: 'rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.4)' }
  },
  '& a, & .MuiLink-root': {
    color: PT.teal,
    textDecorationColor: 'rgba(56,232,255,0.5)'
  },
  '& .MuiCheckbox-root': {
    color: PT.textMuted,
    '&.Mui-checked': { color: PT.teal }
  }
};

// INCRYPT wordmark logo used on the auth panes.
export const AUTH_LOGO_URL =
  'https://incrypt-dynamic-website-images.s3.ap-southeast-2.amazonaws.com/logo.png';
